import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'src/api/instance';
import api from '../api';
import { logReviewLoadFailed } from '../observability';
import { AssetVerificationReviewResponse } from '../ts/interfaces';

type LoadState =
  | { status: 'loading'; data: null }
  | { status: 'loaded'; data: AssetVerificationReviewResponse }
  | { status: 'error'; data: null };

// Loads the VOA review. The requestId ref discards stale in-flight responses
// after a retry, per SpecialtyIncomeCalcPage.
export const useAssetReviewLoad = (entryId: string) => {
  const [state, setState] = useState<LoadState>({ status: 'loading', data: null });
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    setState({ status: 'loading', data: null });
    try {
      const response = await api.getReview(entryId);
      if (requestIdRef.current !== requestId) return;
      setState({ status: 'loaded', data: response });
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      logReviewLoadFailed(
        { entry_id: entryId },
        {
          error_type: isAxiosError(error) && !error.response ? 'network_error' : 'unknown_error',
          http_status: isAxiosError(error) ? error.response?.status : undefined
        }
      );
      setState({ status: 'error', data: null });
    }
  }, [entryId]);

  useEffect(() => {
    if (!entryId) return;
    load();
  }, [entryId, load]);

  return {
    review: state.data,
    isLoading: state.status === 'loading',
    isError: state.status === 'error',
    retry: load
  };
};

export default useAssetReviewLoad;
